import { Link } from 'react-router-dom';
import { Check, Hourglass, Pencil, X } from 'lucide-react';

import type { Industry } from '@/api/types';
import { opportunitiesHref } from '@/routes/business-opportunities/ideaFilters';
import { statusOf } from './industryList';

function IdeasLink({ industry }: { industry: Industry }) {
  if (industry.idea_count === 0) return <span className="hint">0</span>;
  return (
    <Link to={opportunitiesHref({ view: 'list', industries: [industry.id] })} aria-label={`${industry.idea_count} ideas in ${industry.name}`}>
      {industry.idea_count}
    </Link>
  );
}

/** A pending industry shows its badge; an approved one, the Active switch (as Team & Access) for
 *  whoever may edit industries, or a plain Active / Inactive badge for everyone else. */
function StatusCell({
  industry,
  busy,
  onToggle,
}: {
  industry: Industry;
  busy: boolean;
  onToggle: ((active: boolean) => void) | null;
}) {
  if (statusOf(industry) === 'pending') {
    return (
      <span className="badge badge-warning">
        <Hourglass aria-hidden="true" /> Pending review
      </span>
    );
  }
  if (!onToggle) {
    return industry.is_active ? (
      <span className="badge badge-success">Active</span>
    ) : (
      <span className="badge badge-neutral">Inactive</span>
    );
  }
  return (
    <label
      className="switch"
      data-tooltip={industry.is_active ? 'Active. Switch off to hide it from the submit form.' : 'Inactive. Switch on to offer it again.'}
    >
      <input
        type="checkbox"
        checked={industry.is_active}
        disabled={busy}
        aria-label={`${industry.is_active ? 'Deactivate' : 'Reactivate'} ${industry.name}`}
        onChange={(e) => onToggle(e.target.checked)}
      />
      <span className="switch-track" aria-hidden="true" />
    </label>
  );
}

export interface IndustryRowActions {
  canUpdate: boolean;
  canReview: boolean;
  withActions: boolean;
  busy: boolean;
  onToggle: (industry: Industry, active: boolean) => void;
  onApprove: (industry: Industry) => void;
  onReject: (industry: Industry) => void;
  onEdit: (industry: Industry) => void;
}

function RowActions({ industry, actions }: { industry: Industry; actions: IndustryRowActions }) {
  const reviewable = statusOf(industry) === 'pending' && actions.canReview;
  return (
    <div className="row-actions">
      {reviewable && (
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          data-tooltip="Approve and offer it on the submit form"
          aria-label={`Approve ${industry.name}`}
          onClick={() => actions.onApprove(industry)}
        >
          <Check aria-hidden="true" />
        </button>
      )}
      {reviewable && (
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          data-tooltip={industry.idea_count > 0 ? 'Reject and move its ideas to another industry' : 'Reject'}
          aria-label={`Reject ${industry.name}`}
          onClick={() => actions.onReject(industry)}
        >
          <X aria-hidden="true" />
        </button>
      )}
      {/* Edit is always the last action. */}
      {actions.canUpdate && (
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          data-tooltip="Edit"
          aria-label={`Edit ${industry.name}`}
          onClick={() => actions.onEdit(industry)}
        >
          <Pencil aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export function IndustryRow({ industry, actions }: { industry: Industry; actions: IndustryRowActions }) {
  return (
    <tr>
      <td>
        <b>{industry.name}</b>
        {industry.status === 'pending' && industry.suggested_by && (
          <div className="sub">Suggested by {industry.suggested_by.display_name}</div>
        )}
      </td>
      <td className="hint">{industry.description || 'No description'}</td>
      <td className="num"><IdeasLink industry={industry} /></td>
      <td>
        <StatusCell
          industry={industry}
          busy={actions.busy}
          onToggle={actions.canUpdate ? (active) => actions.onToggle(industry, active) : null}
        />
      </td>
      {actions.withActions && (
        <td>
          <RowActions industry={industry} actions={actions} />
        </td>
      )}
    </tr>
  );
}
